import { buildQs } from 'utils/queryString'
import { baseApi } from './baseApi'

export const pluginsPropertyOwnerManageApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // /lessor/v1/plugins-property-owner-manage/property-owners 組織擁有的業主列表
        getPropertyOwners: builder.query({
            query: (data) => {
                const qs = buildQs(data)

                return {
                    url: `lessor/v1/plugins-property-owner-manage/property-owners?${qs}`,
                    method: 'GET',
                }
            },
            providesTags: ['PROPERTY_OWNERS'],
        }),
        // /lessor/v1/plugins-property-owner-manage/property-owners/{propertyOwnerId} 刪除組織業主資料
        deletePropertyOwner: builder.mutation({
            query: (propertyOwnerId) => ({
                url: `lessor/v1/plugins-property-owner-manage/property-owners/${propertyOwnerId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PROPERTY_OWNERS'],
        }),
        // /lessor/v1/plugins-property-owner-manage/property-owners/{propertyOwnerId} 獲取組織業主資料
        getPropertyOwner: builder.query({
            query: (propertyOwnerId) => ({
                url: `lessor/v1/plugins-property-owner-manage/property-owners/${propertyOwnerId}`,
                method: 'GET',
            }),
            keepUnusedDataFor: 0,
            providesTags: ['PROPERTY_OWNER'],
        }),
        // /lessor/v1/plugins-property-owner-manage/property-owners/{propertyOwnerId} 更新組織業主資料
        updatePropertyOwner: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `lessor/v1/plugins-property-owner-manage/property-owners/${id}`,
                method: 'PUT',
                data,
            }),
            invalidatesTags: ['PROPERTY_OWNERS', 'PROPERTY_OWNER'],
        }),
        // /lessor/v1/plugins-property-owner-manage/property-owners 創建組織業主資料
        createPropertyOwner: builder.mutation({
            query: (data) => ({
                url: `lessor/v1/plugins-property-owner-manage/property-owners`,
                method: 'POST',
                data,
            }),
            invalidatesTags: ['PROPERTY_OWNERS'],
        }),
        // /lessor/v1/plugins-property-owner-manage/property-owners/{propertyOwnerId}/warning-status 更新組織業主警告狀態
        updatePropertyOwnerWarningStatus: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `lessor/v1/plugins-property-owner-manage/property-owners/${id}/warning-status`,
                method: 'PUT',
                data,
            }),
            invalidatesTags: ['PROPERTY_OWNER'],
        }),
        /**
         * 取得業主事件列表
         * @param {Object} data - 查詢參數
         * @param {number} [data.take=10] - 取多少筆資料，預設為 10
         * @param {number} [data.skip=0] - 從第幾筆資料開始取，預設為 0
         * @param {string} data.property_owner_id - 業主 ID
         * @returns {Promise<{
         *   total: number,
         *   take: number,
         *   skip: number,
         *   events: Array<{
         *     id: string,
         *     name: string,
         *     created_at: string,
         *     updated_at: string,
         *     creator: {
         *       operator_type: 'SYSTEM',
         *       id: number,
         *       short_id: string,
         *       name: string,
         *       phone: string
         *     }
         *   }>
         * }>} 回傳業主事件列表資料
         */
        getPropertyOwnerEvents: builder.query({
            query: (data) => {
                const qs = buildQs(data)
                return {
                    url: `lessor/v1/plugins-property-owner-manage/property-owners/events?${qs}`,
                }
            },
            keepUnusedDataFor: 0,
            providesTags: ['PROPERTY_OWNER_EVENTS'],
        }),
        /**
         * 創建組織業主事件
         * @param {Object} data - 事件資料
         * @param {string} data.name - 事件名稱
         * @param {string[]} [data.images] - 事件相關圖片列表
         * @param {string[]} [data.files] - 事件相關檔案列表
         * @param {string} [data.description] - 事件描述
         * @param {string} data.property_owner_id - 業主 ID
         * @returns {Promise<{
         *   id: string,
         *   name: string,
         *   created_at: string,
         *   updated_at: string,
         *   creator: {
         *     operator_type: 'SYSTEM',
         *     id: number,
         *     short_id: string,
         *     name: string,
         *     phone: string
         *   }
         * }>} 回傳創建的事件資料
         */
        createPropertyOwnerEvent: builder.mutation({
            query: (data) => ({
                url: `lessor/v1/plugins-property-owner-manage/property-owners/events`,
                method: 'POST',
                data,
            }),
            invalidatesTags: ['PROPERTY_OWNER_EVENTS'],
        }),
        /**
         * 更新組織業主事件資料
         * @param {Object} params - 更新參數
         * @param {string} params.id - 業主事件 ID
         * @param {string} params.name - 事件名稱
         * @param {string[]} [params.images] - 事件相關圖片列表
         * @param {string[]} [params.files] - 事件相關檔案列表
         * @param {string} [params.description] - 事件描述
         * @returns {Promise<void>} 更新成功後回傳
         */
        updatePropertyOwnerEvent: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `lessor/v1/plugins-property-owner-manage/property-owners/events/${id}`,
                method: 'PUT',
                data,
            }),
            invalidatesTags: ['PROPERTY_OWNER_EVENTS'],
        }),
        /**
         * 刪除組織業主事件
         * @param {string} propertyOwnerEventId - 業主事件 ID
         * @returns {Promise<void>} 刪除成功後回傳
         */
        deletePropertyOwnerEvent: builder.mutation({
            query: (propertyOwnerEventId) => ({
                url: `lessor/v1/plugins-property-owner-manage/property-owners/events/${propertyOwnerEventId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PROPERTY_OWNER_EVENTS'],
        }),
        /**
         * 取得業主事件詳細資料
         * @param {string} propertyOwnerEventId - 業主事件ID
         * @returns {Promise<{
         *   id: string,
         *   name: string,
         *   created_at: string,
         *   updated_at: string,
         *   images: string[],
         *   files: string[],
         *   description: string
         * }>} 業主事件詳細資料
         */
        getPropertyOwnerEvent: builder.query({
            query: (propertyOwnerEventId) => ({
                url: `lessor/v1/plugins-property-owner-manage/property-owners/events/${propertyOwnerEventId}`,
                method: 'GET',
            }),
            keepUnusedDataFor: 0,
            providesTags: ['PROPERTY_OWNER_EVENTS'],
        }),
        /**
         * 取得業主歷史記錄
         * @param {string} propertyOwnerId - 業主 ID
         * @returns {Promise<{
         *   records: Array<{
         *     operator: {
         *       operator_type: 'SYSTEM' | 'ADMIN' | 'OPERATOR',
         *       id: number,
         *       short_id: string,
         *       name: string,
         *       phone: string
         *     },
         *     created_at: string,
         *     operation_func: string,
         *     metadata: {
         *       id: string,
         *       is_warning: boolean,
         *     }
         *   }>
         * }>}
         */
        getPropertyOwnerHistories: builder.query({
            query: (propertyOwnerId) => ({
                url: `lessor/v1/plugins-property-owner-manage/property-owners/${propertyOwnerId}/histories`,
                method: 'GET',
            }),
        }),
    }),
})

export const {
    useGetPropertyOwnersQuery,
    useLazyGetPropertyOwnersQuery,
    useGetPropertyOwnersDetailQuery,
    useDeletePropertyOwnerMutation,
    useUpdatePropertyOwnerMutation,
    useGetPropertyOwnerQuery,
    useLazyGetPropertyOwnerQuery,
    useCreatePropertyOwnerMutation,
    useUpdatePropertyOwnerWarningStatusMutation,
    useGetPropertyOwnerEventsQuery,
    useCreatePropertyOwnerEventMutation,
    useUpdatePropertyOwnerEventMutation,
    useDeletePropertyOwnerEventMutation,
    useLazyGetPropertyOwnerEventQuery,
    useGetPropertyOwnerEventQuery,
    useGetPropertyOwnerHistoriesQuery,
} = pluginsPropertyOwnerManageApi
